import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Save, Loader2, Lock, ShieldCheck, Calendar } from 'lucide-react';
import { authAPI } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import AddressAutocomplete from '../AddressAutocomplete';

export default function MyProfileTab() {
  const { user, updateUser } = useAuth();
  const { t, i18n } = useTranslation();
  const loc = i18n.language === 'en' ? 'en-GB' : `${i18n.language}-${i18n.language.toUpperCase()}`;
  const [profileForm, setProfileForm] = useState({
    first_name: '', last_name: '', phone: '', address: '', city: '', postal_code: '', country: 'FR',
  });
  const EU_COUNTRIES = ['FR', 'ES', 'IT', 'BE', 'PT', 'DE', 'NL', 'LU', 'AT', 'IE', 'PL', 'SE', 'DK', 'FI', 'CZ', 'SK', 'SI', 'HR', 'HU', 'RO', 'BG', 'GR', 'EE', 'LV', 'LT', 'CY', 'MT'];
  const countryName = (code) => {
    try { return new Intl.DisplayNames([i18n.language], { type: 'region' }).of(code) || code; } catch { return code; }
  };
  const [savingProfile, setSavingProfile] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current: '', new_password: '', confirm: '' });
  const [savingPassword, setSavingPassword] = useState(false);

  useEffect(() => {
    if (user) {
      setProfileForm({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        phone: user.phone || '',
        address: user.address || '',
        city: user.city || '',
        postal_code: user.postal_code || '',
        country: (user.country || 'FR').toUpperCase(),
      });
    }
  }, [user]);

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      const res = await authAPI.updateProfile(profileForm);
      updateUser(res.data);
      toast.success(t('prof.profile_updated'));
    } catch (err) { toast.error(err.response?.data?.detail || t('prof.error')); }
    finally { setSavingProfile(false); }
  };

  const handleChangePassword = async () => {
    if (passwordForm.new_password !== passwordForm.confirm) {
      toast.error(t('prof.passwords_mismatch'));
      return;
    }
    setSavingPassword(true);
    try {
      await authAPI.changePassword({ current_password: passwordForm.current, new_password: passwordForm.new_password });
      toast.success(t('prof.password_changed'));
      setPasswordForm({ current: '', new_password: '', confirm: '' });
    } catch (err) { toast.error(err.response?.data?.detail || t('prof.error')); }
    finally { setSavingPassword(false); }
  };

  return (
    <div className="max-w-lg space-y-6">
      {/* Account summary */}
      <div className="bg-white rounded-2xl border border-[#D6E8F0] p-5 sm:p-6" data-testid="profile-account-section">
        <div className="flex items-center gap-4 mb-4">
          <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#033871] to-[#0C63BF] flex items-center justify-center text-white text-xl font-bold shrink-0">
            {user?.first_name?.charAt(0)}{user?.last_name?.charAt(0)}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-bold text-[#033871] text-lg truncate">{user?.first_name} {user?.last_name}</p>
            <div className="flex items-center gap-2 mt-0.5">
              <p className="text-sm text-[#5A8BA8] truncate">{user?.email}</p>
              {user?.email_verified ? (
                <span className="inline-flex items-center gap-1 text-[10px] font-bold text-[#32CD32] bg-[#32CD32]/10 px-2 py-0.5 rounded-full shrink-0" data-testid="email-verified-badge">
                  <ShieldCheck className="w-3 h-3" /> {t('prof.verified')}
                </span>
              ) : (
                <Link to="/verify-email" className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full shrink-0 hover:bg-amber-100" data-testid="email-not-verified-badge">
                  {t('prof.not_verified')}
                </Link>
              )}
            </div>
          </div>
        </div>
        {user?.created_at && (
          <div className="flex items-center gap-2 text-xs text-[#B8D4E3]">
            <Calendar className="w-3.5 h-3.5" />
            {t('prof.member_since', { date: new Date(user.created_at).toLocaleDateString(loc, { month: 'long', year: 'numeric' }) })}
          </div>
        )}
      </div>

      {/* Personal info */}
      <div className="bg-white rounded-2xl border border-[#D6E8F0] p-5 sm:p-6" data-testid="profile-info-section">
        <h2 className="font-bold text-[#033871] text-base mb-4">{t('prof.personal_info')}</h2>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="prof-fname">{t('prof.first_name')}</Label>
              <Input id="prof-fname" value={profileForm.first_name} onChange={e => setProfileForm(p => ({ ...p, first_name: e.target.value }))} className="mt-1.5" data-testid="profile-firstname" />
            </div>
            <div>
              <Label htmlFor="prof-lname">{t('prof.last_name')}</Label>
              <Input id="prof-lname" value={profileForm.last_name} onChange={e => setProfileForm(p => ({ ...p, last_name: e.target.value }))} className="mt-1.5" data-testid="profile-lastname" />
            </div>
          </div>
          <div>
            <Label htmlFor="prof-phone">{t('prof.phone')}</Label>
            <Input id="prof-phone" type="tel" value={profileForm.phone} onChange={e => setProfileForm(p => ({ ...p, phone: e.target.value }))} placeholder="06 12 34 56 78" className={`mt-1.5 ${!profileForm.phone ? 'border-red-300' : ''}`} required data-testid="profile-phone" />
            {!profileForm.phone && <p className="text-xs text-red-500 mt-1">{t('prof.phone_required')}</p>}
          </div>
        </div>
      </div>

      {/* Address */}
      <div className="bg-white rounded-2xl border border-[#D6E8F0] p-5 sm:p-6" data-testid="profile-address-section">
        <h2 className="font-bold text-[#033871] text-base mb-1">{t('prof.pickup_address')}</h2>
        <p className="text-xs text-[#5A8BA8] mb-4">{t('prof.pickup_hint')}</p>
        {!user?.address && (
          <div className="flex items-center gap-2 p-3 rounded-xl bg-amber-50 border border-amber-200 mb-4" data-testid="address-missing-warning">
            <MapPin className="w-4 h-4 text-amber-500 shrink-0" />
            <p className="text-xs text-amber-700">{t('prof.address_missing')}</p>
          </div>
        )}
        <div className="space-y-4">
          <div>
            <Label htmlFor="prof-address">{t('prof.address')}</Label>
            <div className="mt-1.5">
              <AddressAutocomplete
                value={profileForm.address}
                onChange={val => setProfileForm(p => ({ ...p, address: val }))}
                onSelect={addr => setProfileForm(p => ({ ...p, address: addr.address, city: addr.city, postal_code: addr.postal_code }))}
                placeholder={t('prof.type_address')}
              />
            </div>
            {!profileForm.address && <p className="text-xs text-red-500 mt-1">{t('prof.address_required')}</p>}
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="prof-postal">{t('prof.postal_code')}</Label>
              <Input id="prof-postal" value={profileForm.postal_code} onChange={e => setProfileForm(p => ({ ...p, postal_code: e.target.value }))} placeholder="75001" className={`mt-1.5 ${!profileForm.postal_code ? 'border-red-300' : ''}`} required data-testid="profile-postal-code" />
            </div>
            <div>
              <Label htmlFor="prof-city">{t('prof.city')}</Label>
              <Input id="prof-city" value={profileForm.city} onChange={e => setProfileForm(p => ({ ...p, city: e.target.value }))} placeholder="Paris" className={`mt-1.5 ${!profileForm.city ? 'border-red-300' : ''}`} required data-testid="profile-city" />
            </div>
          </div>
          <div>
            <Label htmlFor="prof-country">{t('prof.country')}</Label>
            <select
              id="prof-country"
              value={profileForm.country}
              onChange={e => setProfileForm(p => ({ ...p, country: e.target.value }))}
              className="mt-1.5 w-full h-10 px-3 rounded-md border border-input bg-white text-sm"
              data-testid="profile-country"
            >
              {EU_COUNTRIES.map(c => <option key={c} value={c}>{countryName(c)}</option>)}
            </select>
          </div>
        </div>
      </div>

      <Button onClick={handleSaveProfile} disabled={savingProfile} className="w-full bg-[#32CD32] hover:bg-[#28a428] h-11 rounded-xl font-bold gap-2" data-testid="profile-save-btn">
        {savingProfile ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
        {t('prof.save_changes')}
      </Button>

      {/* Change password */}
      <div className="bg-white rounded-2xl border border-[#D6E8F0] p-5 sm:p-6" data-testid="profile-password-section">
        <h2 className="font-bold text-[#033871] text-base mb-1 flex items-center gap-2"><Lock className="w-4 h-4" /> {t('prof.change_password')}</h2>
        <p className="text-xs text-[#5A8BA8] mb-4">{t('prof.password_rules')}</p>
        <div className="space-y-4">
          <div>
            <Label htmlFor="prof-current-pwd">{t('prof.current_password')}</Label>
            <Input id="prof-current-pwd" type="password" value={passwordForm.current} onChange={e => setPasswordForm(p => ({ ...p, current: e.target.value }))} placeholder={t('prof.current_password_ph')} className="mt-1.5" data-testid="profile-current-password" />
          </div>
          <div>
            <Label htmlFor="prof-new-pwd">{t('prof.new_password')}</Label>
            <Input id="prof-new-pwd" type="password" value={passwordForm.new_password} onChange={e => setPasswordForm(p => ({ ...p, new_password: e.target.value }))} placeholder={t('prof.new_password')} className="mt-1.5" data-testid="profile-new-password" />
          </div>
          <div>
            <Label htmlFor="prof-confirm-pwd">{t('prof.confirm_password')}</Label>
            <Input id="prof-confirm-pwd" type="password" value={passwordForm.confirm} onChange={e => setPasswordForm(p => ({ ...p, confirm: e.target.value }))} placeholder={t('prof.confirm_password_ph')} className="mt-1.5" data-testid="profile-confirm-password" />
            {passwordForm.confirm && passwordForm.new_password !== passwordForm.confirm && (
              <p className="text-xs text-red-500 mt-1">{t('prof.passwords_mismatch')}</p>
            )}
          </div>
          <Button onClick={handleChangePassword} disabled={savingPassword || !passwordForm.current || !passwordForm.new_password || passwordForm.new_password !== passwordForm.confirm} variant="outline" className="w-full h-10 rounded-xl font-bold gap-2" data-testid="profile-change-password-btn">
            {savingPassword ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
            {t('prof.change_btn')}
          </Button>
        </div>
      </div>
    </div>
  );
}
